/**
 * Keep contract services (scheduling/jobs) and billing line items linked by default.
 */

export const DSNY_PACKAGE_SERVICE_ID = 'dsny_package'

/** DSNY billing package selected on Maintenance DSNY contracts. */
export type DsnyPackageKind = 'building_upkeep' | 'exterior_maintenance'
/** null = separate visit; undefined = not chosen yet (custom rows must pick). */
export type DsnyCombineTarget = 'dsny' | 'hallway' | null

export interface ContractServiceForLink {
  id: string
  service_item_id: string
  service_type: string
  service_name: string
  amount: number
  description?: string
  scheduled_date?: string | null
  scheduled_time?: string | null
  is_maintenance: boolean
  proposal_line_item_id?: string
  schedule: {
    recurrence_type: string | null
    recurrence_days: number[]
    dsny_integration: boolean
    combine_target?: DsnyCombineTarget
    garbage_schedule: number[]
    recycling_schedule: number[]
    organics_schedule: number[]
    bulk_schedule: number[]
    interior_cleaning_schedule: number[]
    recurrence_day_of_month?: number | null
    recurrence_weekday?: number | null
    recurrence_occurrence?: number | null
    recurrence_month?: number | null
    recurrence_interval_months?: number
    recurrence_interval_weeks?: number
    recurrence_interval_days?: number
  }
}

export interface ContractLineItemForLink {
  id: string
  service_item_id?: string
  proposal_line_item_id?: string
  contract_service_id?: string
  description: string
  quantity: number
  is_variable: boolean
  unit_type: string
  unit_price: number
  total_price: number
  sort_order: number
  linked_service_id?: string
}

export interface DsnyScheduleRow {
  id: string
  type: string
  name: string
  days: number[]
  combine_with_dsny?: boolean
  combine_target?: DsnyCombineTarget
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
  service_item_id?: string
}

/**
 * Resolve visit targets while preserving legacy rows that only stored a boolean.
 * - `'dsny' | 'hallway' | null` = chosen (null = separate visit)
 * - `undefined` = custom row still needs a choice
 */
export function resolveDsnyCombineTarget(
  row: Pick<DsnyScheduleRow, 'type' | 'combine_target' | 'combine_with_dsny'>
): DsnyCombineTarget | undefined {
  if (row.combine_target === 'dsny' || row.combine_target === 'hallway') {
    return row.combine_target
  }
  if (
    Object.prototype.hasOwnProperty.call(row, 'combine_target') &&
    row.combine_target === null
  ) {
    return null
  }
  if (row.combine_with_dsny) return 'dsny'
  // Hallway Cleaning itself belongs in the hallway visit group by default.
  if (row.type === 'hallway_cleaning') return 'hallway'
  // New custom rows must explicitly choose a visit target.
  if (row.type === 'custom') return undefined
  return 'dsny'
}

/** True when a custom/hallway/pickup row still needs a Visit target selection. */
export function dsnyRowNeedsCombineTarget(
  row: Pick<DsnyScheduleRow, 'type' | 'combine_target' | 'combine_with_dsny'>
): boolean {
  return resolveDsnyCombineTarget(row) === undefined
}

export interface CatalogServiceItem {
  id: string
  name: string
  service_category_id?: string
  base_price?: number
  unit_type?: string
}

type GroupedCatalogItem = { id: string; name: string; base_price: number; unit_type: string }
type GroupedCatalog = Array<{ items: GroupedCatalogItem[] }>

export function findBuildingUpkeepItem(
  serviceItemsGrouped: GroupedCatalog
): GroupedCatalogItem | null {
  for (const group of serviceItemsGrouped) {
    const item = group.items.find((i) => {
      const name = i.name.toLowerCase().trim()
      return (
        name === 'building upkeep' ||
        name.includes('building upkeep') ||
        (name.includes('upkeep') && !name.includes('exterior'))
      )
    })
    if (item) return item
  }
  return null
}

export function findExteriorMaintenanceItem(
  serviceItemsGrouped: GroupedCatalog
): GroupedCatalogItem | null {
  for (const group of serviceItemsGrouped) {
    const item = group.items.find((i) => {
      const name = i.name.toLowerCase().trim()
      return name === 'exterior maintenance' || name.includes('exterior maintenance')
    })
    if (item) return item
  }
  return null
}

export function findDsnyPackageItem(
  kind: DsnyPackageKind,
  serviceItemsGrouped: GroupedCatalog
): GroupedCatalogItem | null {
  return kind === 'exterior_maintenance'
    ? findExteriorMaintenanceItem(serviceItemsGrouped)
    : findBuildingUpkeepItem(serviceItemsGrouped)
}

/** Active catalogue items for custom DSNY schedule lines (excludes package parents). */
export function listCatalogItemsForCustomDsnyRows(
  serviceItemsGrouped: GroupedCatalog
): Array<{ id: string; name: string }> {
  const exclude = new Set<string>()
  const building = findBuildingUpkeepItem(serviceItemsGrouped)
  const exterior = findExteriorMaintenanceItem(serviceItemsGrouped)
  if (building) exclude.add(building.id)
  if (exterior) exclude.add(exterior.id)

  const out: Array<{ id: string; name: string }> = []
  for (const group of serviceItemsGrouped) {
    for (const item of group.items || []) {
      if (exclude.has(item.id)) continue
      out.push({ id: item.id, name: item.name })
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name))
}

/** Infer package kind from a persisted contract service or catalogue name. */
export function detectDsnyPackageKind(
  serviceOrName?: { service_name?: string; service_item_id?: string; name?: string } | string | null,
  serviceItemsGrouped?: GroupedCatalog
): DsnyPackageKind {
  const name =
    typeof serviceOrName === 'string'
      ? serviceOrName
      : String(serviceOrName?.service_name || serviceOrName?.name || '').trim()
  if (name.toLowerCase().includes('exterior maintenance')) return 'exterior_maintenance'

  if (serviceItemsGrouped && typeof serviceOrName === 'object' && serviceOrName?.service_item_id) {
    const exterior = findExteriorMaintenanceItem(serviceItemsGrouped)
    if (exterior && serviceOrName.service_item_id === exterior.id) return 'exterior_maintenance'
  }

  return 'building_upkeep'
}

export function defaultDsnyScheduleRowsForKind(kind: DsnyPackageKind): DsnyScheduleRow[] {
  const pickups: DsnyScheduleRow[] = [
    { id: 'garbage', type: 'garbage', name: 'Garbage', days: [], combine_target: 'dsny', combine_with_dsny: true },
    { id: 'recycling', type: 'recycling', name: 'Recycling', days: [], combine_target: 'dsny', combine_with_dsny: true },
    { id: 'organics', type: 'organics', name: 'Organics', days: [], combine_target: 'dsny', combine_with_dsny: true },
    { id: 'bulk', type: 'bulk', name: 'Bulk', days: [], combine_target: 'dsny', combine_with_dsny: true },
  ]
  if (kind === 'exterior_maintenance') return pickups
  return [
    {
      id: 'hallway_cleaning',
      type: 'hallway_cleaning',
      name: 'Hallway Cleaning',
      days: [],
      combine_target: 'hallway',
      combine_with_dsny: false,
    },
    ...pickups,
  ]
}

/** Keep pickup days when switching Building Upkeep ↔ Exterior Maintenance. */
export function rebaseDsnyRowsForPackageKind(
  kind: DsnyPackageKind,
  currentRows: DsnyScheduleRow[]
): DsnyScheduleRow[] {
  return defaultDsnyScheduleRowsForKind(kind).map((row) => {
    const existing = currentRows.find((r) => r.type === row.type)
    if (!existing) return row
    return {
      ...row,
      days: existing.days || [],
      combine_target: resolveDsnyCombineTarget(existing),
      combine_with_dsny: resolveDsnyCombineTarget(existing) === 'dsny',
      assigned_crew_id: existing.assigned_crew_id ?? null,
      assigned_employee_id: existing.assigned_employee_id ?? null,
      persistedServiceId: (existing as any).persistedServiceId,
      recurrence_type: (existing as any).recurrence_type,
      monthly_mode: (existing as any).monthly_mode,
      recurrence_weekday: (existing as any).recurrence_weekday,
      recurrence_occurrence: (existing as any).recurrence_occurrence,
      recurrence_day_of_month: (existing as any).recurrence_day_of_month,
      snap_to_closest_visit: (existing as any).snap_to_closest_visit,
    }
  })
}

/** Apply DSNY API pickup day numbers onto default rows for the selected package. */
export function applyDsnyApiDaysToRows(
  kind: DsnyPackageKind,
  schedules: {
    garbage?: number[]
    recycling?: number[]
    organics?: number[]
    bulk?: number[]
  },
  currentRows: DsnyScheduleRow[] = []
): DsnyScheduleRow[] {
  const byType: Record<string, number[]> = {
    garbage: schedules.garbage || [],
    recycling: schedules.recycling || [],
    organics: schedules.organics || [],
    bulk: schedules.bulk || [],
  }
  return rebaseDsnyRowsForPackageKind(kind, currentRows).map((row) =>
    byType[row.type] !== undefined ? { ...row, days: byType[row.type] } : row
  )
}

export function isDsnyScheduleValid(rows: DsnyScheduleRow[]): boolean {
  return rows.some(
    (row) =>
      (row.days || []).length > 0 &&
      (row.type !== 'custom' || !!(row.service_item_id || (row.name || '').trim()))
  )
}

export function buildLineItemFromContractService(
  service: ContractServiceForLink,
  sortOrder: number
): ContractLineItemForLink {
  const amount = Number(service.amount) || 0
  return {
    id: `linked_${service.id}`,
    service_item_id: service.service_item_id || undefined,
    proposal_line_item_id: service.proposal_line_item_id,
    description: service.service_name || service.description || 'Service',
    quantity: 1,
    is_variable: false,
    unit_type: 'flat_rate',
    unit_price: amount,
    total_price: amount,
    sort_order: sortOrder,
    linked_service_id: service.id,
  }
}

export function upsertLinkedLineItem(
  lineItems: ContractLineItemForLink[],
  service: ContractServiceForLink
): ContractLineItemForLink[] {
  const existingIndex = lineItems.findIndex((li) => li.linked_service_id === service.id)
  const nextItem = buildLineItemFromContractService(
    service,
    existingIndex >= 0 ? lineItems[existingIndex].sort_order : lineItems.length
  )
  if (existingIndex >= 0) {
    nextItem.id = lineItems[existingIndex].id
    const copy = [...lineItems]
    copy[existingIndex] = { ...nextItem, sort_order: lineItems[existingIndex].sort_order }
    return copy
  }
  return [...lineItems, nextItem]
}

export function removeLinkedLineItem(
  lineItems: ContractLineItemForLink[],
  serviceId: string
): ContractLineItemForLink[] {
  return lineItems.filter((li) => li.linked_service_id !== serviceId)
}

export function syncLineItemsWithServices(
  lineItems: ContractLineItemForLink[],
  services: ContractServiceForLink[]
): ContractLineItemForLink[] {
  let result = lineItems.filter(
    (li) => !li.linked_service_id || services.some((s) => s.id === li.linked_service_id)
  )
  services.forEach((service) => {
    result = upsertLinkedLineItem(result, service)
  })
  return result
}

function mapRecurrenceFromProposal(item: { recurrence_type?: string }): {
  recurrenceType: string | null
  recurrenceDays: number[]
} {
  let recurrenceType: string | null = null
  let recurrenceDays: number[] = []

  if (!item.recurrence_type) {
    return { recurrenceType, recurrenceDays }
  }

  switch (item.recurrence_type) {
    case 'one_time':
      recurrenceType = null
      recurrenceDays = []
      break
    case 'daily':
      recurrenceType = 'daily'
      recurrenceDays = [0, 1, 2, 3, 4, 5, 6]
      break
    case 'weekly':
      recurrenceType = 'weekly'
      recurrenceDays = []
      break
    case 'bi_weekly':
      recurrenceType = 'bi_weekly'
      recurrenceDays = []
      break
    case 'monthly':
      recurrenceType = 'monthly'
      break
    case 'quarterly':
      recurrenceType = 'monthly'
      break
    case 'annually':
      recurrenceType = 'monthly'
      break
    case 'custom':
      recurrenceType = 'custom_interval'
      break
    default:
      recurrenceType = null
  }

  return { recurrenceType, recurrenceDays }
}

export function mapProposalLineItemToContractService(
  item: any,
  index: number,
  context: {
    proposal: any
    serviceItems: CatalogServiceItem[]
    isDsnyServiceType: (serviceType: string) => boolean
  }
): ContractServiceForLink {
  const serviceItem = context.serviceItems.find((si) => si.id === item.service_item_id)
  const isMaintenance =
    context.isDsnyServiceType(context.proposal.service_type) ||
    context.isDsnyServiceType(item.service_type) ||
    item.service_type?.toLowerCase().includes('dsny') ||
    false

  const { recurrenceType, recurrenceDays } = item.recurrence_type
    ? mapRecurrenceFromProposal(item)
    : { recurrenceType: null, recurrenceDays: [] }
  const serviceId = `service_${item.id || `${Date.now()}_${index}`}`

  const isOneTime = !item.recurrence_type || item.recurrence_type === 'one_time'
  const scheduledDate = isOneTime
    ? context.proposal.start_date || context.proposal.preferred_start_date || null
    : null

  return {
    id: serviceId,
    service_item_id: item.service_item_id || '',
    service_type: serviceItem?.service_category_id || item.service_type || '',
    service_name: serviceItem?.name || item.description || 'Service',
    amount: item.unit_price || item.total_price || 0,
    description: item.description || '',
    scheduled_date: scheduledDate,
    scheduled_time: null,
    is_maintenance: isMaintenance,
    proposal_line_item_id: item.id,
    schedule: {
      recurrence_type: recurrenceType,
      recurrence_days: recurrenceDays,
      dsny_integration: isMaintenance && Boolean(context.proposal.dsny_integration),
      garbage_schedule: [],
      recycling_schedule: [],
      organics_schedule: [],
      bulk_schedule: [],
      interior_cleaning_schedule: [],
    },
  }
}

export interface DsnyUpkeepProposalInfo {
  proposalLineItemId: string
  amount: number
}

export function mapProposalLineItemsToServicesAndLineItems(
  proposalLineItems: any[],
  proposal: any,
  context: {
    serviceItems: CatalogServiceItem[]
    isDsnyServiceType: (serviceType: string) => boolean
    buildingUpkeepItemId?: string | null
    /** Also treat Exterior Maintenance proposal lines as the deferred DSNY package. */
    exteriorMaintenanceItemId?: string | null
  }
): {
  services: ContractServiceForLink[]
  lineItems: ContractLineItemForLink[]
  /**
   * The proposal's DSNY package line item is not imported directly (the DSNY
   * schedule step creates the merged package instead), but its id and price are
   * surfaced so the contract keeps the price the customer agreed to on the
   * proposal rather than resetting to the catalog base price.
   */
  dsnyUpkeep: DsnyUpkeepProposalInfo | null
} {
  const services: ContractServiceForLink[] = []
  const lineItems: ContractLineItemForLink[] = []
  let dsnyUpkeep: DsnyUpkeepProposalInfo | null = null
  const packageItemIds = new Set(
    [context.buildingUpkeepItemId, context.exteriorMaintenanceItemId].filter(Boolean) as string[]
  )

  proposalLineItems.forEach((item, index) => {
    if (
      item.service_item_id &&
      packageItemIds.has(item.service_item_id) &&
      (proposal.dsny_integration || context.isDsnyServiceType(proposal.service_type))
    ) {
      if (!dsnyUpkeep && item.id) {
        dsnyUpkeep = {
          proposalLineItemId: item.id,
          amount: Number(item.unit_price ?? item.total_price ?? 0) || 0,
        }
      }
      return
    }

    const service = mapProposalLineItemToContractService(item, index, {
      proposal,
      serviceItems: context.serviceItems,
      isDsnyServiceType: context.isDsnyServiceType,
    })

    services.push(service)
    lineItems.push(buildLineItemFromContractService(service, lineItems.length))
  })

  return { services, lineItems, dsnyUpkeep }
}

export function buildDsnyPackageService(
  dsnyScheduleRows: DsnyScheduleRow[],
  options: {
    buildingUpkeepItem?: { id: string; name: string; base_price: number } | null
    maintenanceDSNYCategoryId?: string
    proposalLineItemId?: string | null
    amount?: number
  }
): ContractServiceForLink | null {
  const allDays = Array.from(
    new Set(dsnyScheduleRows.flatMap((row) => row.days || []))
  )
  if (allDays.length === 0) return null

  const amount =
    options.amount ??
    options.buildingUpkeepItem?.base_price ??
    0

  return {
    id: DSNY_PACKAGE_SERVICE_ID,
    service_item_id: options.buildingUpkeepItem?.id || '',
    service_type: options.maintenanceDSNYCategoryId || '',
    service_name: options.buildingUpkeepItem?.name || 'Building Upkeep',
    amount,
    description: options.buildingUpkeepItem?.name || 'Building Upkeep',
    scheduled_date: null,
    scheduled_time: null,
    is_maintenance: true,
    proposal_line_item_id: options.proposalLineItemId || undefined,
    schedule: {
      recurrence_type: 'weekly',
      recurrence_days: allDays,
      dsny_integration: true,
      garbage_schedule: dsnyScheduleRows.find((r) => r.type === 'garbage')?.days || [],
      recycling_schedule: dsnyScheduleRows.find((r) => r.type === 'recycling')?.days || [],
      organics_schedule: dsnyScheduleRows.find((r) => r.type === 'organics')?.days || [],
      bulk_schedule: dsnyScheduleRows.find((r) => r.type === 'bulk')?.days || [],
      interior_cleaning_schedule:
        dsnyScheduleRows.find((r) => r.type === 'hallway_cleaning')?.days || [],
    },
  }
}

export function ensureDsnyPackagePair(
  contractServices: ContractServiceForLink[],
  lineItems: ContractLineItemForLink[],
  dsnyScheduleRows: DsnyScheduleRow[],
  options: {
    buildingUpkeepItem?: { id: string; name: string; base_price: number } | null
    maintenanceDSNYCategoryId?: string
    proposalLineItemId?: string | null
    /** Price agreed on the proposal; wins over the catalog base price. */
    amount?: number
  }
): { contractServices: ContractServiceForLink[]; lineItems: ContractLineItemForLink[] } {
  const pkg = buildDsnyPackageService(dsnyScheduleRows, options)
  if (!pkg) {
    return {
      contractServices: contractServices.filter((s) => s.id !== DSNY_PACKAGE_SERVICE_ID),
      lineItems: removeLinkedLineItem(lineItems, DSNY_PACKAGE_SERVICE_ID),
    }
  }

  const upkeepId = options.buildingUpkeepItem?.id
  const servicesWithoutDupes = contractServices.filter((s) => {
    if (s.id === DSNY_PACKAGE_SERVICE_ID) return true
    if (isDsnyBillingPackageService(s)) return false
    if (upkeepId && s.service_item_id === upkeepId) return false
    return true
  })
  const withoutPackage = servicesWithoutDupes.filter((s) => s.id !== DSNY_PACKAGE_SERVICE_ID)
  const newServices = [...withoutPackage, pkg]

  // All DSNY upkeep is represented by the single Building Upkeep package line item.
  // Drop any other line item that duplicates it, otherwise a stale/duplicate
  // "Building Upkeep" line item (e.g. one linking to a now-collapsed weekly upkeep
  // service) is left orphaned with no resolvable recurrence and trips the B1
  // "no recurrence schedule set" consistency blocker.
  const dsnyUpkeepServiceIds = new Set(
    contractServices
      .filter(
        (s) =>
          s.id !== DSNY_PACKAGE_SERVICE_ID &&
          (s.schedule?.dsny_integration || (s as any).dsny_integration)
      )
      .map((s) => s.id)
  )
  const validServiceIds = new Set(newServices.map((s) => s.id))
  const pkgName = (pkg.service_name || '').trim().toLowerCase()
  let newLineItems = lineItems.filter((li) => {
    // Always drop the old package line item; the single package line item is re-added below.
    if (li.linked_service_id === DSNY_PACKAGE_SERVICE_ID) return false
    const link = li.linked_service_id || li.contract_service_id
    // Line item points at a weekly DSNY upkeep service folded into the package.
    if (link && dsnyUpkeepServiceIds.has(link)) return false
    // Line item is the package's catalog item.
    if (upkeepId && li.service_item_id === upkeepId) return false
    // Orphaned "Building Upkeep" line item whose linked service no longer exists.
    if (
      link &&
      !validServiceIds.has(link) &&
      (li.description || '').trim().toLowerCase() === pkgName
    ) {
      return false
    }
    return true
  })
  newLineItems = upsertLinkedLineItem(newLineItems, pkg)
  newLineItems = syncNonWeeklyDsnyLineItems(newLineItems, newServices)

  return { contractServices: newServices, lineItems: newLineItems }
}

export function removeDsnyPackagePair(
  contractServices: ContractServiceForLink[],
  lineItems: ContractLineItemForLink[]
): { contractServices: ContractServiceForLink[]; lineItems: ContractLineItemForLink[] } {
  return {
    contractServices: contractServices.filter((s) => s.id !== DSNY_PACKAGE_SERVICE_ID),
    lineItems: removeLinkedLineItem(lineItems, DSNY_PACKAGE_SERVICE_ID),
  }
}

/** True for the billing package (Building Upkeep / Exterior), not $0 DSNY children. */
export function isDsnyBillingPackageService(service: any): boolean {
  if (!service) return false
  if (service.id === DSNY_PACKAGE_SERVICE_ID) return true
  const name = String(service.service_name || service.description || '')
    .trim()
    .toLowerCase()
  if (name === 'building upkeep' || name.includes('building upkeep')) return true
  if (name.includes('exterior maintenance')) return true
  const dsny = !!(service.dsny_integration || service.schedule?.dsny_integration)
  return dsny && Number(service.amount) > 0
}

export function isDsnyPackageLineDescription(description?: string | null): boolean {
  const name = String(description || '')
    .trim()
    .toLowerCase()
  return (
    name === 'building upkeep' ||
    name.includes('building upkeep') ||
    name.includes('exterior maintenance')
  )
}

/**
 * Resolve recurrence for consistency checks. DB package rows often store null
 * recurrence (schedule lives on visit groups / DSNY days); in-memory package
 * services nest it under `schedule`.
 */
export function resolveLineItemRecurrenceForConsistency(
  lineItem: { linked_service_id?: string; contract_service_id?: string; description?: string },
  contractServices: any[],
  fallback?: {
    garbage_schedule?: number[]
    recycling_schedule?: number[]
    organics_schedule?: number[]
    bulk_schedule?: number[]
    visit_groups?: Array<{
      recurrence_type?: string | null
      recurrence_days?: number[] | null
    }>
  }
): { recurrence_type: string | null; recurrence_days: number[] } {
  const linked =
    contractServices.find(
      (s: any) =>
        lineItem.linked_service_id === s.id || lineItem.contract_service_id === s.id
    ) ||
    (isDsnyPackageLineDescription(lineItem.description)
      ? contractServices.find((s: any) => isDsnyBillingPackageService(s))
      : undefined)

  const topType = linked?.recurrence_type
  const schedType = linked?.schedule?.recurrence_type
  let recurrence_type: string | null =
    (topType && topType !== 'none' ? topType : null) ||
    (schedType && schedType !== 'none' ? schedType : null) ||
    null

  let recurrence_days: number[] =
    linked?.recurrence_days || linked?.schedule?.recurrence_days || []

  const visitGroups = fallback?.visit_groups || []
  const vgDays = Array.from(
    new Set(
      visitGroups.flatMap((vg) =>
        Array.isArray(vg.recurrence_days) ? vg.recurrence_days.map(Number).filter(Number.isFinite) : []
      )
    )
  ).sort((a, b) => a - b)

  if (recurrence_days.length === 0 && vgDays.length > 0) {
    recurrence_days = vgDays
  }

  const visitGroupHasCadence = visitGroups.some((vg) => {
    const rtype = vg.recurrence_type
    if (!rtype || rtype === 'none') return false
    if (rtype === 'daily' || rtype === 'monthly') return true
    return Array.isArray(vg.recurrence_days) && vg.recurrence_days.length > 0
  })

  if (!recurrence_type) {
    const isPackage =
      isDsnyBillingPackageService(linked) || isDsnyPackageLineDescription(lineItem.description)
    if (isPackage) {
      const hasDays =
        recurrence_days.length > 0 ||
        (fallback?.garbage_schedule || []).length > 0 ||
        (fallback?.recycling_schedule || []).length > 0 ||
        (fallback?.organics_schedule || []).length > 0 ||
        (fallback?.bulk_schedule || []).length > 0 ||
        visitGroupHasCadence
      if (hasDays) {
        const vgType = visitGroups.find((vg) => {
          const rtype = vg.recurrence_type
          if (!rtype || rtype === 'none') return false
          if (rtype === 'daily' || rtype === 'monthly') return true
          return Array.isArray(vg.recurrence_days) && vg.recurrence_days.length > 0
        })?.recurrence_type
        recurrence_type = vgType || 'weekly'
      }
    }
  }

  return { recurrence_type, recurrence_days }
}

function findExistingDsnyUpkeepService(
  contractServices: ContractServiceForLink[],
  upkeepId?: string | null
): ContractServiceForLink | undefined {
  if (upkeepId) {
    const byCatalog = contractServices.find(
      (s) =>
        s.id !== DSNY_PACKAGE_SERVICE_ID &&
        s.service_item_id === upkeepId &&
        (s.is_maintenance ||
          s.schedule?.dsny_integration ||
          (s as any).dsny_integration)
    )
    if (byCatalog) return byCatalog
  }

  // Persisted packages may lack service_item_id or nest dsny only at top level.
  return contractServices.find(
    (s) => s.id !== DSNY_PACKAGE_SERVICE_ID && isDsnyBillingPackageService(s)
  )
}

/**
 * Sync DSNY package in wizard state. Preserves real DB service IDs on edit when possible.
 */
export function syncDsnyPackageInState(
  contractServices: ContractServiceForLink[],
  lineItems: ContractLineItemForLink[],
  dsnyScheduleRows: DsnyScheduleRow[],
  options: {
    buildingUpkeepItem?: { id: string; name: string; base_price: number } | null
    maintenanceDSNYCategoryId?: string
    proposalLineItemId?: string | null
    /** Price agreed on the proposal; wins over the catalog base price. */
    amount?: number
  }
): { contractServices: ContractServiceForLink[]; lineItems: ContractLineItemForLink[] } {
  if (!isDsnyScheduleValid(dsnyScheduleRows)) {
    return removeDsnyPackagePair(contractServices, lineItems)
  }

  const upkeepId = options.buildingUpkeepItem?.id
  const existingReal = findExistingDsnyUpkeepService(contractServices, upkeepId)

  if (existingReal) {
    const pkg = buildDsnyPackageService(dsnyScheduleRows, {
      ...options,
      amount: existingReal.amount,
    })
    if (!pkg) {
      return removeDsnyPackagePair(contractServices, lineItems)
    }

    const updatedService: ContractServiceForLink = {
      ...existingReal,
      service_name: pkg.service_name,
      description: pkg.description,
      amount: pkg.amount,
      is_maintenance: true,
      proposal_line_item_id: existingReal.proposal_line_item_id || pkg.proposal_line_item_id,
      schedule: pkg.schedule,
    }

    const newServices = contractServices
      .filter((s) => s.id !== DSNY_PACKAGE_SERVICE_ID)
      .map((s) => (s.id === existingReal.id ? updatedService : s))

    let newLineItems = removeLinkedLineItem(lineItems, DSNY_PACKAGE_SERVICE_ID)
    newLineItems = upsertLinkedLineItem(newLineItems, updatedService)
    // Non-weekly custom/hallway rows (Drain, monthly Hallway, etc.) need their own
    // line items so job generation can attach them as piggybacks.
    newLineItems = syncNonWeeklyDsnyLineItems(newLineItems, newServices)

    return { contractServices: newServices, lineItems: newLineItems }
  }

  const ensured = ensureDsnyPackagePair(contractServices, lineItems, dsnyScheduleRows, options)
  return {
    contractServices: ensured.contractServices,
    lineItems: syncNonWeeklyDsnyLineItems(ensured.lineItems, ensured.contractServices),
  }
}

/** Ensure standalone non-weekly DSNY services have linked $0 line items for job gen. */
export function syncNonWeeklyDsnyLineItems(
  lineItems: ContractLineItemForLink[],
  services: ContractServiceForLink[]
): ContractLineItemForLink[] {
  let next = lineItems
  for (const service of services) {
    if (service.id === DSNY_PACKAGE_SERVICE_ID) continue
    if (service.schedule?.dsny_integration || (service as any).dsny_integration) continue
    const rtype = service.schedule?.recurrence_type || (service as any).recurrence_type
    if (!rtype || rtype === 'weekly') continue
    next = upsertLinkedLineItem(next, service)
  }
  return next
}

const UUID_ONLY_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Contract service state carries non-UUID sentinels in uuid-typed fields (e.g.
 * proposal template types like "maintenance_dsny" in service_type when the
 * catalog item wasn't matched). Postgres rejects the whole multi-row insert on
 * one bad value, so sanitize at the persistence boundary.
 */
export function toUuidOrNull(value?: string | null): string | null {
  return value && UUID_ONLY_RE.test(value) ? value : null
}

/** True when a contract service row is rebuilt from DSNY schedule rows on save. */
export function isScheduleDerivedContractService(
  service: any,
  buildingUpkeepItem?: { id: string } | null,
  exteriorMaintenanceItem?: { id: string } | null
): boolean {
  const id = String(service?.id || '')
  return (
    id === DSNY_PACKAGE_SERVICE_ID ||
    id.startsWith('dsny_') ||
    (!!buildingUpkeepItem && service.service_item_id === buildingUpkeepItem.id) ||
    (!!exteriorMaintenanceItem && service.service_item_id === exteriorMaintenanceItem.id) ||
    service.dsny_integration === true ||
    service.schedule?.dsny_integration === true
  )
}

/**
 * Map a visit-group assignment id to the persisted contract_services row id.
 * Children keep their own keys (`dsny_<rowId>`); only unresolved ids return null.
 */
export function resolveContractServiceIdForSave(
  tempServiceId: string,
  serviceIdMap: Map<string, string>
): string | null {
  const direct = serviceIdMap.get(tempServiceId)
  if (direct) return direct
  return null
}

/** Register weekly DSNY child temp ids. Children are inserted as their own
 *  contract_services rows — do not collapse them into the package uuid. */
export function expandDsnyPackageServiceIdMap(
  _serviceIdMap: Map<string, string>,
  _dsnyScheduleRows: DsnyScheduleRow[]
): void {
  // No-op: weekly children now persist as real services with their own UUIDs.
}
